'use client';

import { useMemo, useState } from 'react';

import {
  Drawer,
  InlineButton,
  MainButton,
  SecondaryButton,
  SmartInput,
} from '@web/ui';

import styles from './WorkspaceMemberEditDrawer.module.scss';

import type { WorkspaceMemberItem } from '@/features/workspaces/types';
import type { ServiceType } from '@web/types';

const FIXED_ROLE_OPTIONS = [
  { value: 'member', label: 'Member' },
  { value: 'lead', label: 'Lead' },
  { value: 'admin', label: 'Admin' },
  { value: 'owner', label: 'Owner' },
] as const;

export type WorkspaceMemberEditPayload = {
  roleSlug: string;
  monthlyCapacity: number | null;
  monthlySeatCost: number | null;
  typicalServiceTypeIds: string[];
};

type WorkspaceMemberEditDrawerProps = {
  open: boolean;
  member: WorkspaceMemberItem | null;
  serviceTypes: ServiceType[];
  isProcessing: boolean;
  canToggleStatus?: boolean;
  disableRoleChange?: boolean;
  onClose: () => void;
  onExited?: () => void;
  onSave: (payload: WorkspaceMemberEditPayload) => Promise<void>;
  onToggleStatus: (member: WorkspaceMemberItem) => Promise<void>;
};

export function WorkspaceMemberEditDrawer({
  open,
  member,
  serviceTypes,
  isProcessing,
  canToggleStatus = true,
  disableRoleChange = false,
  onClose,
  onExited,
  onSave,
  onToggleStatus,
}: WorkspaceMemberEditDrawerProps) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      onExited={onExited}
      anchor="right"
      width={360}
    >
      {member ? (
        <WorkspaceMemberEditDrawerContent
          key={member.id}
          member={member}
          serviceTypes={serviceTypes}
          isProcessing={isProcessing}
          canToggleStatus={canToggleStatus}
          disableRoleChange={disableRoleChange}
          onClose={onClose}
          onSave={onSave}
          onToggleStatus={onToggleStatus}
        />
      ) : null}
    </Drawer>
  );
}

type WorkspaceMemberEditDrawerContentProps = {
  member: WorkspaceMemberItem;
  serviceTypes: ServiceType[];
  isProcessing: boolean;
  canToggleStatus: boolean;
  disableRoleChange: boolean;
  onClose: () => void;
  onSave: (payload: WorkspaceMemberEditPayload) => Promise<void>;
  onToggleStatus: (member: WorkspaceMemberItem) => Promise<void>;
};

function WorkspaceMemberEditDrawerContent({
  member,
  serviceTypes,
  isProcessing,
  canToggleStatus,
  disableRoleChange,
  onClose,
  onSave,
  onToggleStatus,
}: WorkspaceMemberEditDrawerContentProps) {
  const [draftRoleSlug, setDraftRoleSlug] = useState(
    () => member.role.slug || 'member',
  );
  const [draftMonthlyCapacity, setDraftMonthlyCapacity] = useState<
    number | null
  >(() => member.monthly_capacity ?? null);
  const [draftMonthlySeatCost, setDraftMonthlySeatCost] = useState<
    number | null
  >(() => member.monthly_seat_cost?.quantity ?? null);
  const [draftServiceTypeIds, setDraftServiceTypeIds] = useState<string[]>(() =>
    member.typical_service_types.map((type) => type.id),
  );
  const [serviceTypeSearch, setServiceTypeSearch] = useState('');

  const filteredServiceTypes = useMemo(
    () =>
      serviceTypes.filter((serviceType) =>
        serviceType.label
          .toLowerCase()
          .includes(serviceTypeSearch.toLowerCase()),
      ),
    [serviceTypeSearch, serviceTypes],
  );

  return (
    <div className={styles.memberDrawer}>
      <header className={styles.memberDrawer__header}>
        <span className={styles.memberDrawer__name}>
          {member.user.first_name} {member.user.last_name}
        </span>
        <span className={styles.memberDrawer__email}>{member.user.email}</span>
      </header>

      <div className={styles.memberDrawer__body}>
        <SmartInput
          type="select"
          name="member-role"
          label="Role"
          value={draftRoleSlug}
          options={FIXED_ROLE_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          readOnly={disableRoleChange}
          onChange={(nextValue) => setDraftRoleSlug(String(nextValue))}
        />

        <SmartInput
          type="number"
          name="member-monthly-capacity"
          label="Estimated monthly capacity hours"
          value={draftMonthlyCapacity}
          onChange={setDraftMonthlyCapacity}
        />

        <SmartInput
          type="number"
          name="member-monthly-seat-cost"
          label="Monthly seat cost"
          value={draftMonthlySeatCost}
          onChange={setDraftMonthlySeatCost}
        />

        <div className={styles.memberDrawer__serviceTypesBlock}>
          <SmartInput
            type="text"
            name="member-typical-service-types-search"
            label="Typical service types"
            value={serviceTypeSearch}
            onChange={setServiceTypeSearch}
          />

          <div className={styles.memberDrawer__serviceTypesList}>
            {filteredServiceTypes.map((serviceType) => {
              const isChecked = draftServiceTypeIds.includes(serviceType.id);

              return (
                <div
                  key={serviceType.id}
                  className={styles.memberDrawer__serviceTypeRow}
                >
                  <SmartInput
                    type="checkbox"
                    name={`member-typical-service-type-${serviceType.id}`}
                    label={serviceType.label}
                    checked={isChecked}
                    onChange={(shouldCheck) => {
                      setDraftServiceTypeIds((current) => {
                        if (shouldCheck) {
                          return current.includes(serviceType.id)
                            ? current
                            : [...current, serviceType.id];
                        }

                        return current.filter((id) => id !== serviceType.id);
                      });
                    }}
                  />
                </div>
              );
            })}

            {!filteredServiceTypes.length ? (
              <span>No service types found.</span>
            ) : null}
          </div>
        </div>
      </div>

      <footer className={styles.memberDrawer__footer}>
        {canToggleStatus ? (
          <InlineButton
            ariaLabel={
              member.status === 'active'
                ? 'Deactivate member'
                : 'Activate member'
            }
            onClick={() => {
              void onToggleStatus(member);
            }}
          >
            {member.status === 'active'
              ? 'Deactivate member...'
              : 'Activate member...'}
          </InlineButton>
        ) : null}

        <div className={styles.memberDrawer__footerActions}>
          <SecondaryButton ariaLabel="Cancel member edit" onClick={onClose}>
            Cancel
          </SecondaryButton>

          <MainButton
            isLoading={isProcessing}
            onClick={() => {
              void onSave({
                roleSlug: draftRoleSlug,
                monthlyCapacity: draftMonthlyCapacity,
                monthlySeatCost: draftMonthlySeatCost,
                typicalServiceTypeIds: draftServiceTypeIds,
              });
            }}
          >
            Save
          </MainButton>
        </div>
      </footer>
    </div>
  );
}
